import React, { useContext } from 'react';

import { FatalAttraction as FA } from '@vpr/tracking';

import { translate } from '@vimeo/i18n-runtime';

import AlbumConfigContext from '../../../containers/AlbumConfigContext';
import { Clip } from '../../../types';
import { VIDEO_KEY, STOCK_PRIVACY, SEARCH_PAGE_SIZE } from '../../../utils/constants';
import { idFromUri } from '../../../utils/helpers';
import LiveGridItem from '../../LiveGridItem';
import LoadingSpinner from '../../LoadingSpinner';
import PaginationFooter from '../../PaginationLayout/PaginationFooter';
import { useSearchDispatch } from '../../SearchContext';
import NoResultsIcon from '../icon-noresults.svg';
import {
  Grid,
  MessageWrapper,
  ErrorIcon,
  Paragraph,
  Header,
  Background,
  PaginationWrapper,
} from './SearchGridStyled';

type Props = {
  results: Array<Clip>;
  isLoading: boolean;
  hasErrored: boolean;
  page: number;
  totalVideos: number;
  onPlay: (id: number, isStock?: boolean, video?: Clip) => void;
};

function SearchGrid({ results, hasErrored, isLoading, onPlay, page, totalVideos }: Props) {
  const { navIsHidden } = useContext(AlbumConfigContext);
  const searchDispatch = useSearchDispatch();

  // Worth noting that Search Grid items will always be dark theme, because the grid itself is always black.
  const theme = 'dark';

  return (
    <Background navIsHidden={navIsHidden}>
      {isLoading ? (
        <LoadingSpinner />
      ) : hasErrored ? (
        <MessageWrapper>
          <ErrorIcon />
          <Header>{translate('Please try again')}</Header>
          <Paragraph>{translate('Sorry, something went wrong during your search.')}</Paragraph>
        </MessageWrapper>
      ) : results && results.length > 0 ? (
        <div>
          <Grid navIsHidden={navIsHidden}>
            {results.map((video: Clip) => {
              const videoId = idFromUri(video?.uri, VIDEO_KEY);
              const isStock = video && video.privacy && video.privacy.view === STOCK_PRIVACY;
              return (
                <LiveGridItem
                  key={video.uri}
                  brandColor="#4e4e4e"
                  title={video.name}
                  description={video.description}
                  duration={video.duration}
                  thumbnails={video.pictures}
                  theme={theme}
                  onPlay={() => {
                    FA.trackClick({
                      container: 'showcase',
                      component: 'search',
                      keyword: 'select_video',
                    });
                    onPlay(videoId, isStock, video);
                  }}
                  type=""
                  allowDownloads={false}
                  displayType=""
                  videoId={videoId}
                />
              );
            })}
          </Grid>
          <PaginationWrapper navIsHidden={navIsHidden}>
            <PaginationFooter
              theme={theme}
              currentPage={page}
              totalItems={totalVideos}
              onSetPage={(page) => {
                searchDispatch({ type: 'SEARCH_PAGE', payload: page });
              }}
              pageSize={SEARCH_PAGE_SIZE}
            />
          </PaginationWrapper>
        </div>
      ) : (
        <MessageWrapper>
          <NoResultsIcon />
          <Paragraph>{translate('No results. Try again?')}</Paragraph>
        </MessageWrapper>
      )}
    </Background>
  );
}

export default SearchGrid;
